// src/components/auth/EmailVerificationScreen.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Mail, RefreshCw, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const EmailVerificationScreen = ({ onVerified }) => {
  const { currentUser, resendVerificationEmail, refreshUserProfile, signOut } = useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleResendEmail = async () => {
    try {
      setSending(true);
      setError('');
      setMessage('');
      
      await resendVerificationEmail();
      setMessage('Verification email sent! Please check your inbox.');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to send verification email');
    } finally {
      setSending(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setChecking(true);
      setError('');
      
      await currentUser.reload();
      await refreshUserProfile();
      
      if (currentUser.emailVerified) {
        setMessage('Email verified successfully!');
        setTimeout(() => {
          if (onVerified) onVerified();
        }, 1000);
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      setError('Failed to check verification status');
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-md w-full">
        <div className="bg-slate-800/50 backdrop-blur-xl p-8 rounded-2xl border border-slate-700 shadow-2xl">
          
          {/* Icon */}
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="text-blue-400 h-8 w-8" />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              Verify Your Email
            </h2>
            <p className="text-slate-400 text-sm">
              We've sent a verification link to
            </p>
            <p className="text-white font-medium mt-1">
              {currentUser?.email}
            </p>
          </div>

          {/* Messages */}
          {message && (
            <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 text-sm flex items-center gap-2 animate-in slide-in-from-top-2">
              <CheckCircle size={18} />
              {message}
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2 animate-in slide-in-from-top-2">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {/* Instructions */}
          <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 mb-6">
            <p className="text-slate-300 text-sm leading-relaxed">
              Please check your email inbox and click the verification link to activate your account.
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Don't forget to check your spam folder if you don't see the email.
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleCheckVerification}
              disabled={checking}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20"
            >
              {checking ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  I've Verified My Email
                </>
              )}
            </button>

            <button
              onClick={handleResendEmail}
              disabled={sending}
              className="w-full bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw size={20} />
                  Resend Verification Email
                </>
              )}
            </button>
          </div>

          {/* Sign Out Link */}
          <div className="mt-6 text-center">
            <button
              onClick={handleSignOut}
              className="text-slate-400 hover:text-white text-sm transition-colors"
            >
              Sign out and use a different account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationScreen;